import {
  CollectionReference,
  DocumentData,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { Key } from 'react';
import { db } from '../firebaseSetup';
import { FirestoreDocument } from '../types/firestore-document';

abstract class BaseService<T extends FirestoreDocument> {
  protected collectionRef: CollectionReference<DocumentData>;

  constructor(collectionName: string) {
    this.collectionRef = collection(db, collectionName);
  }

  async add(item: T): Promise<void> {
    const { id, ...itemWithoutId } = item;

    if (item.id) {
      await setDoc(
        doc(this.collectionRef, this.getDocIdAsString(item.id)),
        itemWithoutId
      );
    } else {
      await addDoc(this.collectionRef, item);
    }
  }

  async get(id: string | Key): Promise<T | null> {
    const docRef = doc(this.collectionRef, this.getDocIdAsString(id));
    const docSnap = await getDoc(docRef);

    return docSnap.exists()
      ? { id: docSnap.id, ...(docSnap.data() as T) }
      : null;
  }

  async getAll(): Promise<T[]> {
    const snapshot = await getDocs(this.collectionRef);
    const items: T[] = [];

    snapshot.forEach((doc) => items.push({ id: doc.id, ...(doc.data() as T) }));

    return items;
  }

  async update(item: T): Promise<void> {
    if (!item.id) throw new Error('Cannot update a document without an ID');

    const { id, ...itemWithoutId } = item;

    return await updateDoc(
      doc(this.collectionRef, id.toString()),
      itemWithoutId
    );
  }

  async remove(id: string | Key): Promise<void> {
    const docId = this.getDocIdAsString(id);
    return await deleteDoc(doc(this.collectionRef, docId));
  }

  async removeBatch(ids: (string | Key)[]): Promise<void> {
    const batch = writeBatch(db);

    ids.forEach((id) => {
      const docId = this.getDocIdAsString(id);
      batch.delete(doc(this.collectionRef, docId));
    });

    return await batch.commit();
  }

  listen(callback: (items: T[]) => void) {
    return onSnapshot(this.collectionRef, (snapshot) => {
      const items: T[] = [];
      snapshot.forEach((doc) =>
        items.push({ id: doc.id, ...(doc.data() as T) })
      );
      callback(items);
    });
  }

  protected getDocIdAsString = (id: string | Key): string =>
    typeof id === 'string' ? id : id.toString();
}

export default BaseService;
